import { Modal, SubmitButton, Switch } from "@xola/ui-kit";
import React, { useState } from "react";
import {
    AddOn,
    CatalogItemObject,
    Experience,
    Permission,
    PurchaseRule,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
    PurchaseRuleLinkTag,
    UnitType,
} from "types";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { duplicateExperience, getProcessedPreferences, handleInvalidate } from "./DuplicateExperienceHelper";
import { useForm } from "@/hooks/questionnaireFormHooks";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useCollection } from "@/hooks/useCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { DuplicateSuccessData, getAvailableFeaturesToDuplicate } from "@/models/DuplicateExperience";
import { isVirtualExperience } from "@/models/Experience";

interface DuplicateExperienceModalProps {
    isOpen: boolean;
    originalExperience: Experience;
    onClose: (isConfirmed: boolean, duplicateItems?: DuplicateSuccessData) => void;
}

export const DuplicateExperienceModal = ({ isOpen, originalExperience, onClose }: DuplicateExperienceModalProps) => {
    return (
        <Modal isOpen={isOpen} size="small" onClose={() => onClose(false)}>
            <ModalContent originalExperience={originalExperience} onClose={onClose} />
        </Modal>
    );
};

const ModalContent = ({ originalExperience, onClose }: Omit<DuplicateExperienceModalProps, "isOpen">) => {
    const invalidate = useInvalidate();
    const seller = useSeller();
    const { isGranted } = useAuthorization();
    const { form, isLoading: isLoadingForm } = useForm(originalExperience.id);
    const [selectedItems, setSelectedItems] = useState<Record<string, { selected: boolean }>>({});
    const [isLoading, setIsLoading] = useState(false);

    const { data: schedulePurchaseRuleLinks, isLoading: isLoadingSchedulePurchaseRuleLink } =
        useCollection<PurchaseRuleLink>("purchaseRuleLinks", {
            "tags._id": PurchaseRuleLinkTag.SCHEDULE,
            "entities.id": originalExperience.id,
            expand: "purchaseRule",
            limit: 1,
        });

    const { data: pricingPurchaseRuleLinks, isLoading: isLoadingPricingPurchaseRuleLink } =
        useCollection<PurchaseRuleLink>("purchaseRuleLinks", {
            "tags._id": PurchaseRuleLinkTag.PRICING,
            "entities.id": originalExperience.id,
            expand: "purchaseRule",
            limit: 1,
        });

    const { data: addOns, isLoading: isLoadingAddons } = useCollection<AddOn>("catalogItems", {
        product: originalExperience.id,
        "unitType[in]": `${UnitType.ADDON},${UnitType.PICKUP}`,
        object: CatalogItemObject.PRODUCT_CATALOG_ITEM,
    });

    const { data: taxFeePurchaseRules, isLoading: isLoadingPurchaseRules } = useCollection<PurchaseRule>(
        ["purchaseRules"],
        {
            "tags._id[in]": "fee,tax",
        },
    );

    const purchaseRuleIds = taxFeePurchaseRules?.map(({ id }) => id) ?? [];

    const { data: feePurchaseRuleLinks, isLoading: isLoadingFeePurchaseRuleLinks } = useCollection<PurchaseRuleLink>(
        "purchaseRuleLinks",
        {
            "purchaseRules.purchaseRulePermanentId[in]": purchaseRuleIds.join(","),
            "entities.object[in]": PurchaseRuleLinkEntityObject.EXPERIENCE,
            "entities.id[in]": originalExperience.id,
            expand: "purchaseRule",
        },
        { enabled: purchaseRuleIds?.length > 0 },
    );

    const availableToCopy = getAvailableFeaturesToDuplicate({
        originalExperience,
        seller,
        addOns,
        form,
        feePurchaseRuleLinks,
        virtualMeeting: {
            isGranted: isGranted(Permission.VIRTUAL_MEETINGS_VIEW),
            enabled: isVirtualExperience(originalExperience),
        },
    });

    const handleSelect = (key: string, value: boolean) => {
        setSelectedItems({
            ...selectedItems,
            [key]: {
                selected: value,
            },
        });
    };

    const onSave = async () => {
        setIsLoading(true);

        const experience = await duplicateExperience({
            originalExperience,
            selectedItems,
            seller,
            pricingPurchaseRuleLinks,
            form,
            addOns,
            schedulePurchaseRuleLinks,
            feePurchaseRuleLinks,
        });

        handleInvalidate({ experience, seller, invalidate });

        onClose(true, getProcessedPreferences({ availableToCopy, selectedItems, experience }));
    };

    const isLoadingData =
        isLoadingAddons ||
        isLoadingForm ||
        isLoadingPurchaseRules ||
        isLoadingFeePurchaseRuleLinks ||
        isLoadingPricingPurchaseRuleLink ||
        isLoadingSchedulePurchaseRuleLink;

    return (
        <>
            <Modal.Header>Duplicate Experience</Modal.Header>
            <div className="relative top-5 text-center">
                Description and pricing will be duplicated.
                <br />
                {availableToCopy.length > 0
                    ? "Select additional fields to duplicate."
                    : "No additional fields configured for this experience."}
            </div>
            <Modal.Body className="space-y-4">
                {availableToCopy.map((element) => {
                    return (
                        <div key={element.key} className="block">
                            <Switch.Group>
                                <Switch
                                    isChecked={selectedItems[element.key]?.selected}
                                    size="medium"
                                    onChange={(value: boolean) => handleSelect(element.key, value)}
                                />

                                <Switch.Label direction="left" className="ml-3 font-bold">
                                    {element.label}
                                </Switch.Label>
                            </Switch.Group>
                        </div>
                    );
                })}
            </Modal.Body>

            <Modal.Footer>
                <SecondaryButton onClick={() => onClose(false)}>Cancel</SecondaryButton>
                <SubmitButton disabled={isLoadingData || isLoading} isLoading={isLoading} onClick={onSave}>
                    Confirm
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};
